import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Modal, Input, Alert, Button } from 'antd';
import MapboxGL from 'mapbox-gl';

import FormMap from 'components/FormMap/FormMap';
import Permissions from 'components/Permissions';
import withAuthentication from 'hoc/authentication';
import {
  updateCommentFeature,
  getIntersections,
  updateTempFeatures,
  setActivityUid,
  deleteCommentFeaturesById,
  getRouting,
  setActivities,
} from 'modules/userrequestComment';
import Select from 'components/Fields/Select';
import { getFeatureWithProperties, showTooltipOverFeature } from 'helpers/mapHelpers';
import { getCustomModalProps } from 'helpers/modalHelpers';
import { getActivitiesInfo } from 'helpers/activityHelpers';
import { withLabelProperty, areMatching, addTooltipProperties } from 'helpers/featuresHelpers';
import { getActivityValue } from 'modules/activities-management';
import { changeMapUserrequest, submitData } from 'modules/userrequest';

const localPopup = new MapboxGL.Popup({
  anchor: 'bottom',
  offset: 5,
  closeButton: false,
  closeOnClick: false,
});

class ModalDrawMap extends React.Component {
  componentDidMount () {
    const {
      userrequestList,
      geojson: { features },
      editable,
      userrequestFeatures,
      selectedActivityResponseUid,
      match: { params: { id: userrequestId } },
    } = this.props;
    const hasValidUserrequest = userrequestList && userrequestId;

    this.props.updateTempFeatures(features);

    if (editable && !features.length) {
      const featuresByActivity = userrequestFeatures.filter(({ properties }) =>
        properties.activity === this.selectedActivityId);

      const geojsonFeaturesId = features.map(({ id }) => id);
      this.props.updateTempFeatures([
        ...featuresByActivity.filter(({ id }) => !geojsonFeaturesId.includes(id)),
        ...features,
      ]);
    }

    if (hasValidUserrequest) {
      this.props.setActivities(userrequestList[userrequestId].properties.activities);
    }

    if (selectedActivityResponseUid) {
      this.props.setActivityUid(+selectedActivityResponseUid);
    }
  }

  shouldComponentUpdate (nextProps) {
    const {
      editable,
      geojson,
      userrequestFeatures,
    } = this.props;
    const { selectedActivityResponseUid, selectedActivityUid } = nextProps;

    const nextSelectedActivityId = +selectedActivityResponseUid || +selectedActivityUid;
    const hasDifferentActivityId = nextSelectedActivityId !== this.selectedActivityId;

    if ((hasDifferentActivityId) && editable && !geojson.features.length) {
      const featuresByActivity = userrequestFeatures.filter(({ properties }) =>
        properties.activity === nextSelectedActivityId);

      const geojsonFeaturesId = geojson.features.map(({ id }) => id);
      this.props.updateTempFeatures([
        ...featuresByActivity.filter(({ id }) => !geojsonFeaturesId.includes(id)),
        ...geojson.features,
      ]);
    }
    return true;
  }

  componentDidUpdate (prevProps) {
    const {
      tempFeatures,
      selectedActivityResponseUid,
      selectedActivityUid,
    } = this.props;

    const {
      selectedActivityResponseUid: prevActivityResponseUid,
      selectedActivityUid: prevSelectedActivityUid,
      tempFeatures: prevTempFeatures,
    } = prevProps;

    tempFeatures.forEach((feature, index) => {
      const hasTempFeaturesChanged = prevTempFeatures.length !== tempFeatures.length
      || prevTempFeatures[index].geometry !== tempFeatures[index].geometry;

      const currentSelectedActivityId = +selectedActivityResponseUid || +selectedActivityUid;
      const prevSelectedActivityId = +prevActivityResponseUid || +prevSelectedActivityUid;
      const hasSelectedActivityChanged = currentSelectedActivityId !== prevSelectedActivityId;

      if ((hasTempFeaturesChanged || hasSelectedActivityChanged)
        && !feature.properties.routeInProgress
        && feature.properties.name !== 'LineString') {
        this.props.getIntersections(feature);
      }
    });
  }

  /**
   * @param {*} activityUid
   */
  getEventDatesActivity = activityUid => {
    const {
      userrequestList,
      match: { params: { id } },
    } = this.props;

    if (+activityUid) {
      return userrequestList[id].properties.activities
        .find(activity => activity.uid === +activityUid).eventDates;
    }
    return userrequestList[id].properties.activities[0].eventDates;
  }

  /**
   * updateTempFeatures add or update features in state
   * @param {object} feature
   *
   * @memberof ModalDrawMap
   */
  updateMapFeatures = feature => {
    const featureWithProperties = feature;
    if (feature.geometry.type === 'LineString') {
      featureWithProperties.properties.routeInProgress = true;
      this.props.getRouting(featureWithProperties);
    }
    this.props.updateTempFeatures([
      ...this.props.tempFeatures.filter(({ id }) => feature.properties.id !== id),
      getFeatureWithProperties(feature, this.selectedActivityId),
    ]);
  }

  updateFeatureName = (targetFeature, label) => {
    const { tempFeatures } = this.props;
    this.props.updateTempFeatures([...tempFeatures.map(currentFeature => (
      areMatching(currentFeature, targetFeature)
        ? withLabelProperty(currentFeature, label)
        : currentFeature
    ))]);
  }

  /**
   * deleteTempFeatures delete selected features
   * @param {array} features id array
   *
   * @memberof ModalDrawMap
   */
  deleteTempFeatures = featuresId => {
    this.props.deleteCommentFeaturesById(featuresId);
  }

  handleMapCancel = () => this.props.closeModal();

  handleMapSubmit = () => {
    const { closeModal, tempFeatures } = this.props;
    closeModal();
    this.props.updateCommentFeature(tempFeatures);
  }

  handleMapValid = () => {
    const { closeModal, tempFeatures } = this.props;
    closeModal();
    this.props.updateCommentFeature(tempFeatures);
    this.props.changeMapUserrequest();
    this.props.submitData();
  }

  render () {
    const {
      tempFeatures,
      visible,
      geojson,
      user,
      selectedActivityResponseUid,
      selectedActivityUid,
      editable,
      userrequestList,
      form,
      author,
      firstDraw,
      match: { params: { id: userrequestId } },
    } = this.props;
    const {
      handleMapCancel,
      handleMapSubmit,
      handleMapValid,
      updateMapFeatures,
      deleteTempFeatures,
      getEventDatesActivity,
    } = this;
    const { properties: { selectedActivityUid: { valid: isValidActivity } } } = form;

    const { buttons, ...customModal } = getCustomModalProps(
      user.permissions,
      editable,
      isValidActivity,
      handleMapCancel,
      handleMapSubmit,
      handleMapValid,
    );

    const modalProps = {
      visible,
      onCancel: handleMapCancel,
      footer: buttons && buttons.map(({ label, action, type }) => (
        <Button key={label} type={type} onClick={action}>
          {label}
        </Button>
      )),
      width: '800px',
      title: <h3>Tracé alternatif proposé par ONF</h3>,
      ...customModal,
    };

    const {
      activities,
      activityTypes = this.props.activitySchema,
    } = userrequestList[userrequestId].properties;

    const options = getActivitiesInfo(activities, activityTypes)
      .map(({ uid, value, label }) => ({
        label: `Activité n°${uid + 1} ${getActivityValue(activityTypes, value).label} / ${label}`,
        value: uid,
      }));

    this.selectedActivityId = +selectedActivityResponseUid || +selectedActivityUid;

    const activityFilters = getActivitiesInfo(activities, activityTypes)
      .find(({ uid }) => uid === this.selectedActivityId).filters;

    const activityLabel = (
      <Input
        disabled
        style={{ width: '100%', marginBottom: 10 }}
        label="Activité concernée"
        value={(options.find(({ value }) => value === this.selectedActivityId) || {}).label}
      />
    );

    return (
      <Modal {...modalProps}>
        <Permissions groups={firstDraw && author === user.email ? ['N1', 'N2'] : ['N1']}>
          {(modalProps.editable && options.length > 1)
            ? (
              <Select
                style={{ width: '100%' }}
                label="Choisir l'activité concernée"
                model="userrequestComment.properties.selectedActivityUid"
                options={options}
                placeholder="Choisir l'activité concernée"
                errorMessages={{ required: { message: 'Le choix du type d\'activité est obligatoire' } }}
              />
            )
            : activityLabel}
        </Permissions>

        <Permissions groups={!firstDraw ? ['user', 'N2'] : ['user']}>
          {activityLabel}
        </Permissions>

        {visible && isValidActivity && (
          <FormMap
            showLabel
            features={addTooltipProperties(modalProps.editable ? tempFeatures : geojson.features)}
            drawMode="pointer"
            activity={{ uid: this.selectedActivityId }}
            activityDates={getEventDatesActivity(this.selectedActivityId)}
            activityFilters={activityFilters}
            editable={modalProps.editable}
            updateFeatures={updateMapFeatures}
            updateFeatureName={this.updateFeatureName}
            deleteFeaturesById={deleteTempFeatures}
            mapProps={{
              onMouseMove: showTooltipOverFeature(localPopup),
            }}
            ref={el => {
              this.formMapContainer = el;
            }}
          />
        )}
        <Permissions groups={['N2']}>
          <Alert
            message={(
              <React.Fragment>
                En cliquant sur :
                <ul style={{ padding: 0 }}>
                  <li>
                    <strong>Proposer</strong>, vous incluez ces nouveaux tracés en pièce jointe
                    de votre message dans la discussion. <br />
                  </li>
                  <li>
                    <strong>Imposer</strong>, vous remplacerez les tracés initiaux du demandeur
                    pour l'activité n°{this.selectedActivityId + 1} par ces nouveaux tracés.
                  </li>
                </ul>
              </React.Fragment>
            )}
            type="warning"
            showIcon
          />
        </Permissions>
      </Modal>
    );
  }
}

ModalDrawMap.propTypes = {
  geojson: PropTypes.object.isRequired,
  editable: PropTypes.bool,
  isInternal: PropTypes.bool,
  intersections: PropTypes.object,
  author: PropTypes.string.isRequired,
  firstDraw: PropTypes.bool,
};

ModalDrawMap.defaultProps = {
  editable: false,
  isInternal: false,
  intersections: {},
  firstDraw: false,
};

const StateToProps = (state, ownProps) => ({
  ...ownProps,
  form: state.forms.userrequestComment,
  tempFeatures: state.userrequestComment.tempFeatures || ownProps.geojson.features || [],
  userrequestFeatures: state.userrequest.geojson.features,
  userrequestList: state.userrequestList,
  selectedActivityUid: state.userrequestComment.properties.selectedActivityUid,
  author: ownProps.author || ownProps.user.email,
  activitySchema: state.admin.activities.list,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({
    updateCommentFeature,
    getIntersections,
    setActivityUid,
    getRouting,
    submitData,
    changeMapUserrequest,
    setActivities,
    updateTempFeatures,
    deleteCommentFeaturesById,
  }, dispatch);

export default withRouter(withAuthentication(connect(
  StateToProps, mapDispatchToProps,
)(ModalDrawMap)));



// WEBPACK FOOTER //
// ./src/components/ModalDrawMap/ModalDrawMap.js